import {
  Group,
  User as AltoleapUser
} from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';
import { Employee, IEmployee } from '../employee';
import {
  IUser,
  IUserData,
  IUserFieldError,
  IUserFieldErrorData
} from './user.types';

export class User extends AltoleapUser implements IUser {
  readonly first_name: string;
  readonly last_name: string;
  readonly email: string;
  readonly is_active: boolean = false;
  readonly is_admin: boolean = false;
  readonly employee?: IEmployee;
  readonly groups: Group[] = [];

  constructor(data: IUserData) {
    super(data);

    this.first_name = data.first_name ?? '';

    this.last_name = data.last_name ?? '';

    this.email = data.email ?? '';

    if (data.is_active) {
      this.is_active = data.is_active;
    }

    if (data.is_admin) {
      this.is_admin = data.is_admin;
    }

    if (data.employee) {
      this.employee = new Employee(data.employee);
    }

    if (data.groups) {
      this.groups = data.groups.map(group => new Group(group));
    }
  }

  validate(): boolean {
    if (this.first_name && this.last_name && this.email) {
      return true;
    } else {
      return false;
    }
  }

  get fullName(): string {
    return `${this.first_name} ${this.last_name}`.trim();
  }

  get intitials(): string {
    return this.fullName
      .toUpperCase()
      .split(' ')
      .map(n => n[0])
      .join('');
  }

  /**
   * Checks if the user has the specified permission.
   *
   * @param permission - The name of the permission to check.
   * @returns - True if the user has the specified permission, otherwise false.
   */
  hasPermission(permission: string): boolean {
    return !!this.permissions[permission] || this.is_admin;
  }

  /**
   * Checks if the user has any of the specified permissions.
   *
   * @param permissions - An array of permission names to check against.
   * @returns - True if the user has any of the specified permissions, otherwise false.
   */
  hasPermissions(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Checks if the user has any of the specified roles.
   *
   * @param roles - An array of role names to check against.
   * @returns - True if the user has any of the specified roles or is an admin, otherwise false.
   */
  hasRoles(roles: string[]): boolean {
    // Check if any of the user's groups match the specified roles
    const userHasRole = this.groups.some(group => roles.includes(group.name!));

    // Return true if the user has any of the specified roles or if the user is an admin
    return userHasRole || this.is_admin;
  }

  toString() {
    return this.fullName;
  }
}

export class UserFieldError implements IUserFieldError {
  readonly first_name: string[] = [];
  readonly last_name: string[] = [];
  readonly email: string[] = [];
  readonly is_active: string[] = [];

  constructor(data?: IUserFieldErrorData) {
    if (data) {
      if (data.first_name) {
        this.first_name = data.first_name;
      }
      if (data.last_name) {
        this.last_name = data.last_name;
      }
      if (data.email) {
        this.email = data.email;
      }
      if (data.is_active) {
        this.is_active = data.is_active;
      }
    }
  }

  clearErrors(): void {
    this.first_name.splice(0, this.first_name.length);
    this.last_name.splice(0, this.last_name.length);
    this.email.splice(0, this.email.length);
    this.is_active.splice(0, this.is_active.length);
  }

  static isUserFieldError(data: IUserFieldErrorData): boolean {
    throw new Error('Method not implemented.');
  }
}
